import { useRef, useState } from 'react';
import { Plus, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api';

// Réduit la photo (1600 px max, JPEG 0.8) avant l'envoi : ~300 Ko au lieu de plusieurs Mo.
function shrink(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(img.src);
      resolve(canvas.toDataURL('image/jpeg', 0.8));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });
}

export default function AddButton() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const send = async (body) => {
    setBusy(true);
    try {
      await api.post('/cockpit/inbox', body);
      toast.success('Noté — je le range à mon prochain passage');
      setText(''); setOpen(false);
    } catch (e) {
      toast.error(e.status === 413 ? 'Photo trop grande' : e.message);
    } finally { setBusy(false); }
  };
  const onPhoto = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    let photo;
    try { photo = await shrink(f); } catch { toast.error("Ce n'est pas une image lisible"); return; }
    send({ type: 'photo', photo, text: text.trim() || null });
  };

  return (
    <>
      {open && (
        <div className="fixed inset-x-3 bottom-24 lg:inset-x-auto lg:right-6 lg:w-96 z-40 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 space-y-2" data-testid="add-panel">
          <div className="flex items-center justify-between">
            <span className="font-extrabold font-manrope text-slate-900">Idée, tâche ou photo</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Fermer" className="text-slate-400"><X size={18} /></button>
          </div>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3} autoFocus data-testid="add-text"
            placeholder="Écris ou dicte avec le micro du clavier… Je range au bon endroit."
            className="w-full rounded-xl border border-slate-200 px-3 py-2 text-base" />
          <div className="grid grid-cols-2 gap-2">
            <button type="button" disabled={!text.trim() || busy} onClick={() => send({ type: 'texte', text: text.trim() })} data-testid="add-send"
              className="rounded-xl bg-[#0288D1] text-white font-semibold py-2.5 disabled:opacity-40">Ajouter</button>
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
              className="rounded-xl border border-slate-200 text-slate-700 font-semibold py-2.5 flex items-center justify-center gap-1.5"><Camera size={16} /> Photo</button>
          </div>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} />
        </div>
      )}
      {!open && <button type="button" onClick={() => setOpen(true)} data-testid="btn-add"
        className="fixed right-4 bottom-24 lg:bottom-6 lg:right-6 z-40 flex items-center gap-2 bg-[#0288D1] text-white rounded-full px-4 py-3 font-bold shadow-lg shadow-[#0288D1]/30">
        <Plus size={18} /> Idée / tâche
      </button>}
    </>
  );
}
